/**
 * 6 - a 'numerical' rating service with several stars — numerical services have `min_stars` (`0` or `1`) and `max_stars` (`1` to `20`) ; like/dislike and numerical services have `star_shape`, which is one of `"circle"` | `"square"` | `"fat star"` | `"pentagram star"`
 * 7 - a 'like/dislike' rating service with on/off status — like/dislike and numerical services have `star_shape`, which is one of `"circle"` | `"square"` | `"fat star"` | `"pentagram star"`
 * 22 - a 'inc/dec' rating service with positive integer rating
 */
export enum RatingServiceType {
  Stars = 6,
  Like = 7,
  Counter = 22,
}

type RatingServiceTypeStars = RatingServiceType.Stars;
type RatingServiceTypeLike = RatingServiceType.Like;
type RatingServiceTypeCounter = RatingServiceType.Counter;

export type StarShape =
  | 'circle'
  | 'square'
  | 'fat star'
  | 'pentagram star'
  | 'six point star'
  | 'eight point star'
  | 'x shape'
  | 'diamond'
  | 'heart'
  | 'svg';

export type RatingColour = {
  brush: string; // fill / background colour
  pen: string; // stroke / text colour
};

export type RatingColourState = 'like' | 'dislike' | 'null' | 'mixed';

export type RatingColours = Partial<Record<RatingColourState, RatingColour>>;

type BaseService = {
  name: string; // "fav"
  service_key: string; // "6c6f63616c2074616773"
  type: RatingServiceType; // 22
  type_pretty: string; // "local inc/dec rating service",
  colours?: RatingColours;
};

type GraphicalRatingService = BaseService & {
  star_shape: StarShape;
};

export type StarsService = GraphicalRatingService & {
  type: RatingServiceTypeStars;
  min_stars?: 0 | 1;
  // eslint-disable-next-line prettier/prettier
  max_stars?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20;
};

export type LikeService = GraphicalRatingService & {
  type: RatingServiceTypeLike;
};

export type CounterService = BaseService & {
  type: RatingServiceTypeCounter;
};

export type HasValue = {
  value: number | boolean | null;
};

export type CounterValue = number;
export type LikeValue = boolean | null;
export type StarsValue = number | null;
export type AnyValue = CounterValue | LikeValue | StarsValue;

/* eslint-disable prettier/prettier */
export type CounterServiceIncludingValue = CounterService & HasValue & {
  value: CounterValue;
};
export type LikeServiceIncludingValue = LikeService & HasValue & {
  value: LikeValue;
};
export type StarsServiceIncludingValue = StarsService & HasValue & {
  value: StarsValue;
};
/* eslint-enable prettier/prettier */

export type AnyRatingService = CounterService | LikeService | StarsService;

export type AnyRatingServiceIncludingValue =
  | CounterServiceIncludingValue
  | LikeServiceIncludingValue
  | StarsServiceIncludingValue;

/**
 * Generic service (any type) returned by /get_services, with service_key injected.
 * Includes all service types: rating services, tag services, etc.
 */
export type AnyService = {
  service_key: string;
  name: string;
  type: number;
  type_pretty?: string;
  [key: string]: unknown;
};

export function isObject(obj: any): obj is object {
  return obj !== null && typeof obj === 'object';
}

export function isBaseService(obj: any): obj is BaseService {
  if (!isObject(obj)) {
    return false;
  }
  const { name, service_key, type, type_pretty } = <BaseService>obj;
  return (
    type !== undefined &&
    service_key !== undefined &&
    type_pretty !== undefined &&
    name !== undefined
  );
}

export function isStarsService(obj: any): obj is StarsService {
  if (!isObject(obj)) {
    return false;
  }
  const { type } = <StarsService>obj;
  return isBaseService(obj) && type === RatingServiceType.Stars;
}

export function isLikeService(obj: any): obj is LikeService {
  if (!isObject(obj)) {
    return false;
  }
  const { type } = <LikeService>obj;
  return isBaseService(obj) && type === RatingServiceType.Like;
}

export function isCounterService(obj: any): obj is CounterService {
  if (!isObject(obj)) {
    return false;
  }
  const { type } = <CounterService>obj;
  return isBaseService(obj) && type === RatingServiceType.Counter;
}

export function hasValue(obj: any): obj is HasValue {
  if (!isObject(obj)) {
    return false;
  }
  const { value } = <HasValue>obj;
  return value !== undefined;
}

export function isLikeValue(value: any): value is LikeValue {
  return value === null || value === true || value === false;
}

// eslint-disable-next-line prettier/prettier
export function isStarsServiceIncludingValue(obj: any): obj is StarsServiceIncludingValue {
  return isStarsService(obj) && hasValue(obj);
}

// eslint-disable-next-line prettier/prettier
export function isLikeServiceIncludingValue(obj: any): obj is LikeServiceIncludingValue {
  return isLikeService(obj) && hasValue(obj) && isLikeValue(obj.value);
}

// eslint-disable-next-line prettier/prettier
export function isCounterServiceIncludingValue(obj: any): obj is CounterServiceIncludingValue {
  return isCounterService(obj) && hasValue(obj);
}
